import { useState, useRef } from 'react';
import { Button } from '@/components/ui/button';
import { notesApi } from '@/services/notes';
import { BusinessCardNoteRequest, BusinessCardFormData } from '@/types';
import toast from 'react-hot-toast';

interface BusinessCardCaptureProps {
  meetingId: number;
  meetingDate: string;
  meetingTime: string;
  location: string;
  onComplete?: (noteId: number) => void;
}

export const BusinessCardCapture = ({
  meetingId,
  onComplete,
}: BusinessCardCaptureProps) => {
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageBase64, setImageBase64] = useState<string | null>(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const [extractedData, setExtractedData] = useState<BusinessCardFormData | null>(null);
  const [isSaved, setIsSaved] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isCameraActive, setIsCameraActive] = useState(false);

  const handleFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    if (!file.type.startsWith('image/')) {
      toast.error('Wybierz plik obrazu');
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const result = e.target?.result as string;
      setImagePreview(result);
      // Remove data:image/jpeg;base64, prefix
      const base64 = result.split(',')[1];
      setImageBase64(base64);
    };
    reader.readAsDataURL(file);
  };

  const startCamera = async () => {
    try {
      console.log('🎥 Starting camera...');

      // Check if mediaDevices API is available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        toast.error('Twoja przeglądarka nie obsługuje dostępu do kamery');
        console.error('MediaDevices API not available');
        return;
      }

      // Check camera permission status (if supported)
      try {
        if ('permissions' in navigator) {
          const permissionStatus = await navigator.permissions.query({ name: 'camera' as PermissionName });
          console.log('📋 Camera permission status:', permissionStatus.state);

          if (permissionStatus.state === 'denied') {
            toast.error(
              'Dostęp do kamery jest zablokowany. Włącz uprawnienia w ustawieniach przeglądarki: Ustawienia → Prywatność i bezpieczeństwo → Uprawnienia witryn → Kamera',
              { duration: 8000 }
            );
            return;
          }
        }
      } catch (permError) {
        console.log('⚠️ Permission API not fully supported, continuing anyway');
      }

      // Request camera access with simplified constraints for better Android compatibility
      console.log('📸 Requesting camera access...');
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: 'environment', // Prefer back camera
          // Simplified constraints - let device choose optimal resolution
        }
      });

      console.log('✅ Camera stream obtained:', stream.getVideoTracks());

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setIsCameraActive(true);

        // Wait for video metadata to load with timeout
        console.log('⏳ Waiting for video metadata...');
        const metadataPromise = new Promise<void>((resolve) => {
          if (videoRef.current) {
            videoRef.current.onloadedmetadata = () => {
              console.log('✅ Metadata loaded');
              resolve();
            };
          } else {
            resolve();
          }
        });

        const timeoutPromise = new Promise<void>((resolve) =>
          setTimeout(() => {
            console.log('⏰ Metadata timeout - continuing anyway');
            resolve();
          }, 3000)
        );

        await Promise.race([metadataPromise, timeoutPromise]);

        // Explicitly play the video (required for some mobile browsers)
        if (videoRef.current) {
          try {
            console.log('▶️ Playing video...');
            await videoRef.current.play();
            console.log('✅ Video playing');
            toast.success('Kamera uruchomiona!');
          } catch (playError) {
            console.error('Video play error:', playError);
            toast.error('Nie można uruchomić podglądu kamery');
          }
        }
      }
    } catch (error: any) {
      console.error('❌ Camera error:', error);

      // Better error messages based on error type
      if (error.name === 'NotAllowedError') {
        toast.error(
          'Dostęp do kamery został odrzucony. Aby włączyć: Ustawienia przeglądarki → Uprawnienia witryn → Kamera → Zezwól',
          { duration: 8000 }
        );
      } else if (error.name === 'NotFoundError') {
        toast.error('Nie znaleziono kamery na urządzeniu');
      } else if (error.name === 'NotReadableError') {
        toast.error('Kamera jest używana przez inną aplikację');
      } else if (error.name === 'OverconstrainedError') {
        toast.error('Nie można spełnić wymagań kamery. Spróbuj ponownie.');
      } else {
        toast.error(`Nie można uruchomić kamery: ${error.message || 'nieznany błąd'}`);
      }
    }
  };

  const stopCamera = () => {
    if (videoRef.current?.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setIsCameraActive(false);
    }
  };

  const capturePhoto = () => {
    if (!videoRef.current || !canvasRef.current) return;

    const video = videoRef.current;
    const canvas = canvasRef.current;
    const context = canvas.getContext('2d');

    if (!context) return;

    // Business card standard aspect ratio (CR80: 85.60mm × 53.98mm)
    const BUSINESS_CARD_ASPECT = 1.586;
    const CROP_PERCENTAGE = 0.95; // Use 95% of available space to match frame

    const videoAspect = video.videoWidth / video.videoHeight;

    let cropWidth, cropHeight;

    if (videoAspect > BUSINESS_CARD_ASPECT) {
      // Video is wider than business card - constrain by height
      cropHeight = video.videoHeight * CROP_PERCENTAGE;
      cropWidth = cropHeight * BUSINESS_CARD_ASPECT;
    } else {
      // Video is taller than business card - constrain by width
      cropWidth = video.videoWidth * CROP_PERCENTAGE;
      cropHeight = cropWidth / BUSINESS_CARD_ASPECT;
    }

    // Center the crop area
    const cropX = (video.videoWidth - cropWidth) / 2;
    const cropY = (video.videoHeight - cropHeight) / 2;

    // Set canvas to cropped dimensions only
    canvas.width = cropWidth;
    canvas.height = cropHeight;

    // Draw only the cropped business card area
    context.drawImage(
      video,
      cropX, cropY, cropWidth, cropHeight,  // Source rectangle (from video)
      0, 0, cropWidth, cropHeight           // Destination rectangle (to canvas)
    );

    const dataUrl = canvas.toDataURL('image/jpeg', 0.95); // 95% quality
    setImagePreview(dataUrl);
    const base64 = dataUrl.split(',')[1];
    setImageBase64(base64);

    stopCamera();
  };

  const handleSubmit = async () => {
    if (!imageBase64) {
      toast.error('Wybierz lub zrób zdjęcie wizytówki');
      return;
    }

    setIsProcessing(true);

    try {
      const noteData: BusinessCardNoteRequest = {
        meetingId,
        imageBase64,
      };

      const response = await notesApi.uploadBusinessCard(noteData);

      if (response.success && response.note.businessCard) {
        const card = response.note.businessCard;
        setExtractedData({
          companyName: card.companyName,
          personName: card.personName,
          position: card.position,
          phoneMobile: card.phoneMobile,
          phoneLandline: card.phoneLandline,
          email: card.email,
          street: card.street,
          city: card.city,
          postalCode: card.postalCode,
          country: card.country,
          website: card.website,
        });
        setIsSaved(true);
        toast.success('Wizytówka przetworzona przez AI!');

        if (onComplete) {
          onComplete(response.note.id);
        }
      }
    } catch (error) {
      toast.error('Błąd podczas przetwarzania wizytówki');
      console.error('Business card error:', error);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleClear = () => {
    if (window.confirm('Czy na pewno chcesz usunąć zdjęcie?')) {
      setImagePreview(null);
      setImageBase64(null);
      setExtractedData(null);
      setIsSaved(false);
      stopCamera();
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleNewCard = () => {
    setImagePreview(null);
    setImageBase64(null);
    setExtractedData(null);
    setIsSaved(false);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-2">
        <h3 className="text-base sm:text-lg font-semibold text-foreground">Przechwytywanie wizytówek</h3>
        <div className="text-xs sm:text-sm text-muted-foreground">
          OCR przez AI
        </div>
      </div>

      {!isSaved ? (
        <>
          {/* Image preview or camera */}
          <div className="space-y-3 sm:space-y-4">
            <div className="relative border-2 border-dashed border-border rounded-lg p-3 sm:p-8 text-center bg-card">
              {!imagePreview && !isCameraActive ? (
                <div className="space-y-3 sm:space-y-4">
                  <p className="text-xs sm:text-base text-muted-foreground px-2">Wybierz lub zrób zdjęcie wizytówki</p>

                  <div className="flex flex-col gap-2 justify-center px-2">
                    <Button
                      onClick={() => fileInputRef.current?.click()}
                      variant="outline"
                      className="w-full text-sm sm:text-base py-2 sm:py-2"
                    >
                      📁 Wybierz plik
                    </Button>

                    <Button
                      onClick={startCamera}
                      variant="outline"
                      className="w-full text-sm sm:text-base py-2 sm:py-2"
                    >
                      📷 Użyj kamery
                    </Button>
                  </div>

                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    capture="environment"
                    onChange={handleFileSelect}
                    className="hidden"
                  />
                </div>
              ) : isCameraActive ? (
                <div className="space-y-3 sm:space-y-4">
                  {/* Camera preview with business card aspect ratio guide */}
                  <div className="relative w-full mx-auto overflow-hidden" style={{ maxWidth: '100%' }}>
                    <video
                      ref={videoRef}
                      autoPlay
                      playsInline
                      muted
                      className="w-full rounded-lg bg-black"
                      style={{
                        minHeight: '300px',
                        maxHeight: '70vh',
                        display: 'block',
                        objectFit: 'cover'
                      }}
                    />
                    {/* Enhanced business card framing guide - landscape rectangle */}
                    <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                      {/* Darkened overlay outside the frame */}
                      <div className="absolute inset-0 bg-black/40" />

                      {/* Business card frame - standard CR80 aspect ratio (85.60mm × 53.98mm ≈ 1.586:1) */}
                      {/* Responsive container: full width with max constraint and safe padding */}
                      <div className="relative z-10 w-full max-w-2xl mx-auto px-4">
                        <div
                          className="border-4 border-white rounded-lg shadow-2xl relative bg-transparent"
                          style={{
                            paddingBottom: '63%', /* 63% = 1/1.586 for business card aspect ratio */
                          }}
                        >
                          {/* Corner guides for precise alignment */}
                          <div className="absolute top-0 left-0 w-8 h-8 border-t-4 border-l-4 border-green-400" />
                          <div className="absolute top-0 right-0 w-8 h-8 border-t-4 border-r-4 border-green-400" />
                          <div className="absolute bottom-0 left-0 w-8 h-8 border-b-4 border-l-4 border-green-400" />
                          <div className="absolute bottom-0 right-0 w-8 h-8 border-b-4 border-r-4 border-green-400" />

                          {/* Instruction text - full width within padded container */}
                          <div className="absolute -top-10 inset-x-0 text-center">
                            <div className="bg-black/70 rounded py-2 px-3 text-white text-xs sm:text-sm font-semibold inline-block max-w-full">
                              📇 Umieść wizytówkę POZIOMO w ramce
                            </div>
                          </div>

                          {/* Centering crosshair */}
                          <div className="absolute inset-0 flex items-center justify-center">
                            <div className="w-full h-px bg-white/30" />
                            <div className="absolute w-px h-full bg-white/30" />
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2 justify-center px-2">
                    <Button onClick={capturePhoto} className="w-full text-sm sm:text-base py-2 sm:py-2 bg-green-600 hover:bg-green-700">
                      📸 Zrób zdjęcie
                    </Button>
                    <Button onClick={stopCamera} variant="outline" className="w-full text-sm sm:text-base py-2 sm:py-2">
                      Anuluj
                    </Button>
                  </div>
                </div>
              ) : (
                <div className="space-y-3 sm:space-y-4">
                  <div className="w-full mx-auto" style={{ maxWidth: '600px' }}>
                    <img
                      src={imagePreview ?? undefined}
                      alt="Business card preview"
                      className="w-full rounded-lg object-contain"
                      style={{ maxHeight: '400px' }}
                    />
                  </div>
                  <Button onClick={handleClear} variant="outline" size="sm" className="w-full sm:w-auto">
                    Zmień zdjęcie
                  </Button>
                </div>
              )}
            </div>
          </div>

          <canvas ref={canvasRef} className="hidden" />

          {/* Submit button */}
          {imagePreview && !isProcessing && (
            <div className="flex flex-col gap-2 px-2">
              <Button onClick={handleSubmit} disabled={!imageBase64} className="w-full text-sm sm:text-base py-2 sm:py-2">
                {isProcessing ? 'Przetwarzanie przez AI...' : 'Przetwórz wizytówkę'}
              </Button>
              <Button onClick={handleClear} variant="outline" className="w-full text-sm sm:text-base py-2 sm:py-2">
                Wyczyść
              </Button>
            </div>
          )}

          {isProcessing && (
            <div className="text-center text-xs sm:text-sm text-muted-foreground px-2">
              <p>Przetwarzanie wizytówki przez AI...</p>
              <p className="text-xs mt-1">To może potrwać kilka sekund</p>
            </div>
          )}
        </>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Image */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground">Zdjęcie wizytówki</h4>
              <img
                src={imagePreview || ''}
                alt="Business card"
                className="w-full border border-border rounded-md"
              />
            </div>

            {/* Extracted data */}
            <div className="space-y-2">
              <h4 className="text-sm font-medium text-foreground flex items-center gap-2">
                Wyodrębnione dane
                <span className="text-xs text-accent">✓</span>
              </h4>
              <div className="p-4 bg-card border border-border rounded-md space-y-2">
                {extractedData?.companyName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Firma</p>
                    <p className="text-sm font-medium">{extractedData.companyName}</p>
                  </div>
                )}
                {extractedData?.personName && (
                  <div>
                    <p className="text-xs text-muted-foreground">Osoba</p>
                    <p className="text-sm font-medium">{extractedData.personName}</p>
                  </div>
                )}
                {extractedData?.position && (
                  <div>
                    <p className="text-xs text-muted-foreground">Stanowisko</p>
                    <p className="text-sm font-medium">{extractedData.position}</p>
                  </div>
                )}
                {extractedData?.phoneMobile && (
                  <div>
                    <p className="text-xs text-muted-foreground">Tel. komórkowy</p>
                    <p className="text-sm font-medium">{extractedData.phoneMobile}</p>
                  </div>
                )}
                {extractedData?.phoneLandline && (
                  <div>
                    <p className="text-xs text-muted-foreground">Tel. stacjonarny</p>
                    <p className="text-sm font-medium">{extractedData.phoneLandline}</p>
                  </div>
                )}
                {extractedData?.email && (
                  <div>
                    <p className="text-xs text-muted-foreground">Email</p>
                    <p className="text-sm font-medium">{extractedData.email}</p>
                  </div>
                )}
                {extractedData?.website && (
                  <div>
                    <p className="text-xs text-muted-foreground">Strona WWW</p>
                    <p className="text-sm font-medium">{extractedData.website}</p>
                  </div>
                )}
                {extractedData?.street && (
                  <div>
                    <p className="text-xs text-muted-foreground">Ulica</p>
                    <p className="text-sm font-medium">{extractedData.street}</p>
                  </div>
                )}
                {(extractedData?.city || extractedData?.postalCode) && (
                  <div>
                    <p className="text-xs text-muted-foreground">Miasto</p>
                    <p className="text-sm font-medium">
                      {extractedData.postalCode && `${extractedData.postalCode} `}
                      {extractedData.city}
                    </p>
                  </div>
                )}
                {extractedData?.country && (
                  <div>
                    <p className="text-xs text-muted-foreground">Kraj</p>
                    <p className="text-sm font-medium">{extractedData.country}</p>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2">
            <Button onClick={handleNewCard} className="w-full sm:w-auto">Dodaj kolejną wizytówkę</Button>
          </div>
        </>
      )}
    </div>
  );
};
